import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MagnifyingGlass, Heart, Clock, Shield, Star } from '@phosphor-icons/react';

const HomePage = () => {
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="relative py-20 px-4" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-6">
              <h1 className="font-heading font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-foreground leading-tight">
                Assistenza Sanitaria
                <br />
                <span className="text-primary">Quando Ne Hai Bisogno</span>
              </h1>
              <p className="text-lg text-foreground/80 leading-relaxed max-w-2xl">
                Connettiti con professionisti sanitari qualificati vicino a te.
                Infermieri, fisioterapisti, psicologi e badanti verificati per la tua famiglia.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/request" className="flex-1 sm:flex-initial">
                  <Button size="lg" className="w-full rounded-full" data-testid="find-professional-btn">
                    <MagnifyingGlass size={20} className="mr-2" />
                    Trova un Professionista
                  </Button>
                </Link>
                <Link to="/register" className="flex-1 sm:flex-initial">
                  <Button size="lg" variant="outline" className="w-full rounded-full" data-testid="become-professional-btn">
                    Diventa Professionista
                  </Button>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="neumorphism rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1542037104857-ffbb0b9155fb"
                  alt="Famiglia che si tiene per mano"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-muted" id="how-it-works">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-medium text-2xl sm:text-3xl lg:text-4xl tracking-tight text-foreground mb-4">
              Come Funziona
            </h2>
            <p className="text-lg text-foreground/70">Tre semplici passaggi per trovare l&apos;assistenza di cui hai bisogno</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="neumorphism p-8 rounded-2xl text-center space-y-4" data-testid="feature-search">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <MagnifyingGlass size={32} className="text-primary" weight="duotone" />
              </div>
              <h3 className="font-heading font-medium text-xl">1. Cerca</h3>
              <p className="text-foreground/70">
                Trova professionisti qualificati nella tua zona usando i nostri filtri avanzati.
              </p>
            </div>

            <div className="neumorphism p-8 rounded-2xl text-center space-y-4" data-testid="feature-connect">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto">
                <Heart size={32} className="text-accent" weight="duotone" />
              </div>
              <h3 className="font-heading font-medium text-xl">2. Connetti</h3>
              <p className="text-foreground/70">
                Visualizza profili dettagliati, recensioni e prenota direttamente online.
              </p>
            </div>

            <div className="neumorphism p-8 rounded-2xl text-center space-y-4" data-testid="feature-receive">
              <div className="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mx-auto">
                <Shield size={32} className="text-accent" weight="duotone" />
              </div>
              <h3 className="font-heading font-medium text-xl">3. Ricevi Assistenza</h3>
              <p className="text-foreground/70">
                Professionisti verificati si prenderanno cura di te e della tua famiglia.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4" id="services">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-medium text-2xl sm:text-3xl lg:text-4xl tracking-tight text-foreground mb-4">
              I Nostri Servizi
            </h2>
            <p className="text-lg text-foreground/70">Professionisti qualificati per ogni esigenza assistenziale</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Assistenza Infermieristica', icon: '🩺' },
              { name: 'Fisioterapia', icon: '🦿' },
              { name: 'Supporto Psicologico', icon: '🧠' },
              { name: 'Badanti', icon: '👵' },
              { name: 'OSS/OSA', icon: '🏥' },
              { name: 'Visite Geriatriche', icon: '👨‍⚕️' },
              { name: 'Logopedia', icon: '🗣️' },
              { name: 'Assistenza Domiciliare', icon: '🏠' },
            ].map((service, index) => (
              <div
                key={index}
                className="neumorphism p-6 rounded-2xl hover:-translate-y-1 transition-transform duration-200 cursor-pointer"
                data-testid={`service-${index}`}
              >
                <div className="text-4xl mb-3">{service.icon}</div>
                <h3 className="font-heading font-medium text-lg">{service.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories Carousel — inserito prima della CTA */}

      {/* CTA Section */}
      <section className="py-20 px-4 bg-primary text-white">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="font-heading font-medium text-2xl sm:text-3xl lg:text-4xl tracking-tight">
            Pronto a Trovare l&apos;Assistenza Giusta?
          </h2>
          <p className="text-lg text-white/90">
            Inizia ora a cercare professionisti sanitari qualificati nella tua zona.
          </p>
          <Link to="/request">
            <Button size="lg" variant="secondary" className="rounded-full" data-testid="cta-search-btn">
              Richiedi Assistenza Ora
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-white py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6">
            <span className="font-heading font-bold text-2xl">VivaLink Italia</span>
          </div>
          <p className="text-white/70 mb-4">
            Connettendo famiglie e professionisti sanitari in tutta Italia
          </p>
          <p className="text-white/50 text-sm">
            © 2026 VivaLink Italia. Tutti i diritti riservati.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;